import type { ComponentProps, FunctionComponent } from 'react';
import styled from 'styled-components';
import {
  Button,
  Link,
  mediaHelper,
  paletteHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import { getFeatureButtonStyle } from '../../../app-kit/helpers/featureButtonStyle';

const CTABannerContent = styled.div<{ $imagePlacement?: 'left' | 'right' }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  column-gap: 4.5rem;
  padding-inline: ${spacingHelper('medium')};

  ${mediaHelper('tabletMin')} {
    flex-direction: row;
    padding-inline: ${spacingHelper('extraLarge')};
  }

  ${mediaHelper('desktopMin')} {
    flex-direction: row;
    padding-inline: 0;
    ${(props) => props.$imagePlacement === 'left' && 'flex-direction: row-reverse;'}
  }
`;

const CTABanner = styled.div`
  width: 850px;
  margin: 0 auto;

  ${mediaHelper('tabletMax')} {
    overflow: hidden;
  }
`;

const CTASubHeading = styled.h2`
  ${typographyHelper('overline')};
  color: ${paletteHelper('textSecondary')};
`;

const CTATitle = styled.h3`
  ${typographyHelper('title03')};
  color: ${paletteHelper('textPrimary')};
`;

const CTASubTitle = styled.p`
  ${typographyHelper('body01')};
  color: ${paletteHelper('textPrimary')};
`;

const CTAContent = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${spacingHelper('small')};
  padding-top: ${spacingHelper('twoExtraLarge')};
  min-width: 50%;
  margin-block: auto;

  ${mediaHelper('tabletMin')} {
    padding-block: ${spacingHelper('twoExtraLarge')};
    align-items: flex-start;
  }
`;

const CTAPicture = styled.picture`
  margin-inline: auto;
  margin-top: ${spacingHelper('extraLarge')};
  ${mediaHelper('desktopMin')} {
    margin-top: ${spacingHelper('twoExtraLarge')};
  }
`;

const CTAImage = styled.img`
  aspect-ratio: 0.9543;
  height: 285px;

  ${mediaHelper('desktopMin')} {
    height: 335px;
    width: auto;
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: ${spacingHelper('extraSmall')};
  margin-top: ${spacingHelper('small')};
`;

export const CTALink = styled(Link)`
  white-space: nowrap;
  flex-grow: 1;
  ${getFeatureButtonStyle()}
`;

export const CTAButton = styled(Button)`
  white-space: nowrap;
  flex-grow: 1;

  ${getFeatureButtonStyle()}
`;

type ActionItemProps = (ComponentProps<typeof CTAButton> | ComponentProps<typeof CTALink>) & {
  id: string;
};

const isCKLInk = (
  componentProps: ActionItemProps,
): componentProps is ComponentProps<typeof CTALink> & { id: string } => 'href' in componentProps;

interface CallToActionBrickProps {
  subheading: string;
  title: string;
  subtitle: string;
  image: {
    avifImage: string;
    pngImage: string;
    alt: string;
  };
  imagePlacement?: 'left' | 'right';
  actionItems?: ActionItemProps[];
}

export const CallToActionBrick: FunctionComponent<CallToActionBrickProps> = ({
  subheading,
  title,
  subtitle,
  image,
  imagePlacement = 'right',
  actionItems,
}) => {
  return (
    <CTABanner>
      <CTABannerContent $imagePlacement={imagePlacement}>
        <CTAContent>
          <CTASubHeading>{subheading}</CTASubHeading>
          <CTATitle>{title}</CTATitle>
          <CTASubTitle>{subtitle}</CTASubTitle>
          {actionItems && (
            <ActionsWrapper>
              {actionItems?.map((actionItem) => {
                if (isCKLInk(actionItem)) {
                  return <CTALink key={actionItem.id} {...actionItem} />;
                } else {
                  return <CTAButton key={actionItem.id} {...actionItem} />;
                }
              })}
            </ActionsWrapper>
          )}
        </CTAContent>
        <CTAPicture>
          <source src={image.avifImage} type="image/avif" />
          <CTAImage src={image.pngImage} alt={image.alt} />
        </CTAPicture>
      </CTABannerContent>
    </CTABanner>
  );
};
